import {
  AdMob,
  BannerAdPosition,
  BannerAdSize,
  MaxAdContentRating,
} from '@capacitor-community/admob';
import { MONETIZATION } from '../config';
import { trackEvent } from '../events';
import { AdsProvider, RewardResult } from '../ads';

export class AdmobAdsProvider implements AdsProvider {
  private ready = false;
  private bannerVisible = false;

  async initialize(): Promise<void> {
    await AdMob.initialize({
      initializeForTesting: MONETIZATION.testingAds,
      tagForChildDirectedTreatment: MONETIZATION.childDirected,
      tagForUnderAgeOfConsent: MONETIZATION.childDirected,
      maxAdContentRating: MaxAdContentRating.General,
    });
    this.ready = true;
    trackEvent('monetization_init', { provider: 'admob' });
  }

  async showBanner(): Promise<void> {
    if (!this.ready || this.bannerVisible) {
      return;
    }
    await AdMob.showBanner({
      adId: MONETIZATION.ids.bannerAdUnit,
      adSize: BannerAdSize.ADAPTIVE_BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      margin: 0,
      isTesting: MONETIZATION.testingAds,
    });
    this.bannerVisible = true;
    trackEvent('ad_banner_shown', { provider: 'admob' });
  }

  async hideBanner(): Promise<void> {
    if (!this.bannerVisible) {
      return;
    }
    await AdMob.hideBanner();
    this.bannerVisible = false;
    trackEvent('ad_banner_hidden', { provider: 'admob' });
  }

  async showInterstitial(): Promise<boolean> {
    if (!this.ready) {
      return false;
    }
    try {
      await AdMob.prepareInterstitial({
        adId: MONETIZATION.ids.interstitialAdUnit,
        isTesting: MONETIZATION.testingAds,
      });
      await AdMob.showInterstitial();
      trackEvent('ad_interstitial_shown', { provider: 'admob' });
      return true;
    } catch {
      trackEvent('ad_interstitial_skipped', { provider: 'admob', reason: 'unavailable' });
      return false;
    }
  }

  async prepareRewarded(): Promise<boolean> {
    if (!this.ready) {
      return false;
    }
    try {
      await AdMob.prepareRewardVideoAd({
        adId: MONETIZATION.ids.rewardedAdUnit,
        isTesting: MONETIZATION.testingAds,
      });
      return true;
    } catch {
      return false;
    }
  }

  isRewardedReady(): boolean {
    return this.ready;
  }

  async showRewarded(): Promise<RewardResult> {
    if (!this.ready) {
      trackEvent('ad_rewarded_unavailable', { provider: 'admob' });
      return 'unavailable';
    }
    trackEvent('ad_rewarded_requested', { provider: 'admob' });
    try {
      const reward = await AdMob.showRewardVideoAd();
      if (reward && reward.amount > 0) {
        trackEvent('ad_rewarded_granted', { provider: 'admob' });
        return 'granted';
      }
      trackEvent('ad_rewarded_dismissed', { provider: 'admob' });
      return 'dismissed';
    } catch {
      trackEvent('ad_rewarded_unavailable', { provider: 'admob' });
      return 'failed';
    }
  }
}
